import { useEffect, useRef } from 'react';
import { TIME_WHEEL_PICKER_SNAP_SCROLL_DEBOUNCE_MS } from 'constants/time-wheel-picker';
import { debounce } from 'utils/helpers/debounce';

export type UseSlowSnapScrollOptions = {
  speed?: number;
  debounceMs?: number;
  /** When false, listeners are not attached and the debounced snap callback no-ops. */
  enabled?: boolean;
  /**
   * When this ref is true, debounced scroll-to-snap commits are skipped (e.g. during
   * programmatic scrollIntoView). User wheel clears it inside the hook.
   */
  suppressScrollCommitRef?: React.MutableRefObject<boolean>;
};

const isScrollContainerUsable = (container: HTMLElement) => {
  if (!document.body.contains(container)) {
    return false;
  }
  if (container.getClientRects().length === 0) {
    return false;
  }
  const { width, height } = container.getBoundingClientRect();
  return width > 0 && height > 0;
};

export const useSlowSnapScroll = (
  containerRef: React.RefObject<HTMLElement>,
  itemRefs: React.MutableRefObject<Record<string, HTMLElement | null>>,
  onChange: (val: string) => void,
  options?: UseSlowSnapScrollOptions
) => {
  const {
    speed = 0.2,
    debounceMs = TIME_WHEEL_PICKER_SNAP_SCROLL_DEBOUNCE_MS,
    enabled = true,
    suppressScrollCommitRef
  } = options ?? {};

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const handleScroll = useRef(
    debounce(() => {
      if (!enabledRef.current) {
        return;
      }

      if (suppressScrollCommitRef?.current) {
        return;
      }

      const container = containerRef.current;
      if (!container || !isScrollContainerUsable(container)) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const containerCenterY = containerRect.top + containerRect.height / 2;

      let closestItem: { value: string; distance: number } = {
        value: '',
        distance: Infinity
      };

      Object.entries(itemRefs.current).forEach(([itemValue, element]) => {
        if (!element) {
          return;
        }

        const rect = element.getBoundingClientRect();
        const itemCenterY = rect.top + rect.height / 2;
        const distance = Math.abs(itemCenterY - containerCenterY);

        if (distance < closestItem.distance) {
          closestItem = { value: itemValue, distance };
        }
      });

      if (closestItem.distance !== Infinity) {
        onChangeRef.current(closestItem.value);
      }
    }, debounceMs)
  ).current;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      if (suppressScrollCommitRef) {
        suppressScrollCommitRef.current = false;
      }

      event.preventDefault();
      container.scrollBy({
        top: event.deltaY * speed,
        behavior: 'auto'
      });

      handleScroll();
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('scroll', handleScroll);
    };
    // Ref identity is stable; listing it would not change effect behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- suppressScrollCommitRef intentionally omitted
  }, [containerRef, enabled, handleScroll, speed]);
};
